import React, {useEffect, useState} from "react"
import cx from "classnames"
import {useQuery} from "@tanstack/react-query"
import {useTranslation} from "react-i18next"
import {studentService} from "services"
import {handleError} from "helpers"
import {useVisible} from "hooks"
import {Icon} from "components/Icon"
import CircularSpin from "components/CircularSpin/CircularSpin"
import {BasePagination} from "uiKit"
import {Order, PaginationPerPage} from "types/common"
import {Auth} from "types/auth"
import {StudentChecklistItem} from "types/students"
import {StudentDepartmentChecklistItem} from "./StudentDepartmentChecklistItem"
import styles from "./StudentDepartmentChecklist.module.css"

type Props = {
    profileId: number
    departmentId?: number
    student?: Auth.UserProfile
}

const PAGE_SIZE = PaginationPerPage.item10

export function StudentDepartmentChecklist(props: Props) {
    const {profileId, departmentId, student} = props
    const {t} = useTranslation(["common", "user"])
    const checklistVisible = useVisible(true)
    const [page, setPage] = useState(1)

    useEffect(() => {
        setPage(1)
    }, [profileId, departmentId])

    const {data, isLoading, isFetching, refetch} = useQuery(
        ["student-department-checklist", profileId, departmentId, page, PAGE_SIZE],
        async () => {
            const response = await studentService.getAllChecklist({
                filter: {
                    profileId,
                    ...(departmentId !== undefined ? {departmentId} : {}),
                    isArchived: false
                },
                range: {
                    page,
                    pageSize: PAGE_SIZE
                },
                sort: {
                    orderBy: "position",
                    orderDir: Order.Asc
                },
                linkedEntities: true
            })
            return {
                items: (response?.data ?? []) as StudentChecklistItem[],
                total: response?.total ?? 0,
                profileId,
                departmentId
            }
        },
        {
            enabled: !!profileId,
            onError: (error) => handleError(error)
        }
    )

    const isDataForCurrentStudent = data?.profileId === profileId && data?.departmentId === departmentId
    const items = isDataForCurrentStudent ? data.items : []
    const total = isDataForCurrentStudent ? data.total : 0
    const showLoading = isLoading || (isFetching && !isDataForCurrentStudent)

    useEffect(() => {
        if (total > 0 && (page - 1) * PAGE_SIZE >= total) {
            setPage(1)
        }
    }, [total, page])

    if (!profileId) {
        return null
    }

    return (
        <div className={styles.wrap}>
            <div className={styles.headingWrap}>
                <div className={styles.headingTitle}>{t("user:user.checklist")}</div>
                <button
                    className={cx(styles.collapseButton, {[styles.collapsed]: checklistVisible.isVisible})}
                    onClick={() => checklistVisible.toggle()}>
                    <Icon icon="ARROW_DOWN" color="#666666" className={styles.headingIcon} />
                </button>
            </div>
            {checklistVisible.isVisible && (
                <>
                    {total > 0 && (
                        <BasePagination
                            page={page}
                            total={total}
                            defaultPageSize={PAGE_SIZE}
                            onChangePage={setPage}
                            displayMode="panel"
                        />
                    )}
                    {showLoading ? (
                        <div className={styles.loading}>
                            <CircularSpin size="small" />
                        </div>
                    ) : items.length === 0 ? (
                        <div className={styles.empty}>{t("checklist.empty")}</div>
                    ) : (
                        <div className={styles.list}>
                            {items.map((item) => (
                                <StudentDepartmentChecklistItem
                                    key={item.studentChecklistItemId}
                                    item={item}
                                    profileId={profileId}
                                    departmentId={departmentId}
                                    student={student}
                                    onReviewed={refetch}
                                />
                            ))}
                        </div>
                    )}
                </>
            )}
        </div>
    )
}
